import React, { useState, useEffect } from 'react'
import { book, getBookingOptions } from '../service/studyService'

const DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const dayLabel = (iso) => {
  const d = new Date(iso)
  return `${DAY[d.getDay()]}, ${MONTH[d.getMonth()]} ${d.getDate()}`
}
const timeLabel = (iso) => new Date(iso)
  .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

// Combine a chosen calendar day with a chosen time-of-day into one ISO instant.
const combine = (dayISO, hhmm) => {
  const d = new Date(dayISO)
  const [h, m] = (hhmm || '').split(':').map(Number)
  if (!Number.isFinite(h)) return null
  d.setHours(h, m || 0, 0, 0)
  return d.toISOString()
}

// Follow-up booking gate — the session cannot complete until a day 6–8 slot is booked.
// Presents the three eligible days as concrete choices rather than an open datetime
// field: participants were otherwise free to pick any date, and an empty field silently
// booked "six days from now" without anyone choosing it.
export default function BookingGateStep ({ step, participantId, onAdvance, busy }) {
  const [options, setOptions] = useState(null)
  const [dayISO, setDayISO] = useState('')
  const [time, setTime] = useState('10:00')
  const [bookedAt, setBookedAt] = useState(step?.bookingAt || null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getBookingOptions(participantId)
      .then(o => {
        setOptions(o)
        if (o.bookingAt) setBookedAt(o.bookingAt)
        if (o.days?.length) setDayISO(o.days[1]?.dateISO || o.days[0].dateISO)   // default: day 7
      })
      .catch(e => setError(e.response?.data?.error || e.message))
  }, [participantId])

  const doBook = async () => {
    setError('')
    const slotISO = combine(dayISO, time)
    if (!slotISO) { setError('Please choose a day and a time.'); return }
    setSaving(true)
    try {
      const r = await book(participantId, slotISO)
      setBookedAt(r.bookingAt)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally { setSaving(false) }
  }

  return (
    <div className="study-card">
      <h2 className="study-h2">Book your follow-up</h2>
      <p>
        Pick your 20-minute follow-up slot,{' '}
        {options ? `${options.minDays}–${options.maxDays} days from now` : '6–8 days from now'}.
        The bonus is attached to completing it. This session cannot close until a slot is booked.
      </p>

      {!options && !error && <p className="study-hint">Loading available days…</p>}

      {options && (
        <>
          <div className="study-slots">
            {options.days.map(d => (
              <button
                key={d.dateISO}
                className={`study-slot${dayISO === d.dateISO ? ' is-on' : ''}`}
                onClick={() => setDayISO(d.dateISO)}
                disabled={saving}
              >
                <span className="study-slot__day">{dayLabel(d.dateISO)}</span>
                <span className="study-slot__off">day {d.dayOffset}</span>
              </button>
            ))}
          </div>

          <div className="study-actions">
            <label className="study-hint" htmlFor="slot-time">Time</label>
            <input
              id="slot-time"
              type="time"
              className="study-input"
              value={time}
              onChange={e => setTime(e.target.value)}
              disabled={saving}
            />
            <button className="study-btn" onClick={doBook} disabled={saving || !dayISO}>
              {bookedAt ? 'Change slot' : 'Book slot'}
            </button>
          </div>
        </>
      )}

      {error && <p className="study-err">{error}</p>}

      {bookedAt && (
        <p className="study-ok">
          ✓ Booked for <b>{dayLabel(bookedAt)} at {timeLabel(bookedAt)}</b> — you can finish the session.
        </p>
      )}

      <div className="study-actions">
        <button
          className="study-btn study-btn--primary"
          disabled={!bookedAt || busy}
          onClick={() => onAdvance({ booked: true, bookingAt: bookedAt })}
        >
          Finish session
        </button>
        {!bookedAt && <span className="study-hint">book a slot to continue</span>}
      </div>
    </div>
  )
}
